import React, { memo, useMemo, useCallback, useEffect } from 'react';
import { TMChangeOrderPreviewPageBloc } from './TMChangeOrderPreviewPageBloc';
import { formGroup, formControl, useFormConfig, useFormState } from '@core/utils/form';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { TMChangeOrderLineResponse } from '@business/entities/tm';
import { PDFPageLayout } from '@modules/public/PublicPageLayout';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { useHistory, useQueryStringParam } from '@core/router';
import { Formatter } from '@core/utils/formatter';
import { Endpoint } from '@business/api/endpoint';
import { AppConfig } from '@core/utils/config';
import { Card, CardTitle, Col, Container, Row } from 'reactstrap';
import { Button } from '@core/components/Button';
import { useBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import './index.scss';
import { FormInput } from '@core/components/form';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Table } from '@core/components/Table';
import { ColumnType } from 'antd/es/table';
import { COService } from '@business/services';
import { PageHeader } from '@modules/common';

function defineTMCOSignatureForm() {
  return formGroup({
    sign: formControl<string>({
      label: 'Signature',
      required: true,
    }),
  });
}

export const TMChangeOrderPreviewPage = memo(() => {
  const param = useQueryStringParam('q') ?? '';
  const decodedParams = new URLSearchParams(atob(param));
  const appId = decodedParams.get('appId') ?? '0';
  const companyId = decodedParams.get('companyId') ?? '0';
  const proposalId = decodedParams.get('proposalId') ?? '0';
  const senderId = decodedParams.get('senderId') ?? '0';
  const coId = decodedParams.get('coId') ?? '0';
  const viewedEmail = decodedParams.get('senderEmail') ?? '';
  AppConfig.set({ appId });
  Endpoint.setAppId(appId);

  const bloc = useBloc(TMChangeOrderPreviewPageBloc, coId);
  const form = useMemo(() => defineTMCOSignatureForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const loading = useLoadingState(bloc.loading);
  const item = useNonNilObservable(bloc.item$);
  const history = useHistory();

  useEffect(() => {
    if (item) {
      form.patchValue({ sign: item.sign });
    }
  }, [form, item]);

  const lItems = useMemo(() => item?.items ?? [], [item]);

  const columns: ColumnType<TMChangeOrderLineResponse>[] = useMemo(() => {
    return [
      {
        title: 'Category',
        dataIndex: 'category',
        sortDirections: ['descend', 'ascend'],
        render: (_, item) => item.category.title,
      },
      {
        title: 'Product/Custom',
        dataIndex: 'product.title',
        sortDirections: ['descend', 'ascend'],
        render: (_, item) => item.product?.title ?? item.customTitle,
      },
      {
        title: 'Qty',
        dataIndex: 'quantity',
        sortDirections: ['descend', 'ascend'],
      },
      // {
      //   title: 'Unit Price',
      //   dataIndex: 'unitPrice',
      //   sortDirections: ['descend', 'ascend'],
      //   render: (_, item) => Formatter.formatCurrency(item.unitPrice, 'USD'),
      // },
      {
        title: 'Labor Price',
        dataIndex: 'laborPrice',
        sortDirections: ['descend', 'ascend'],
        render: (_, item) => Formatter.formatCurrency(item.laborPrice, 'USD'),
      },
      {
        title: 'Material Price',
        dataIndex: 'materialPrice',
        sortDirections: ['descend', 'ascend'],
        render: (_, item) => Formatter.formatCurrency(item.materialPrice, 'USD'),
      },
      {
        title: 'Markup %',
        dataIndex: 'markupFactor',
        sortDirections: ['descend', 'ascend'],
        render: (_, item) => Formatter.formatCurrency(item.markupFactor),
      },
      {
        title: 'Markup $',
        dataIndex: 'totalMarkup',
        sortDirections: ['descend', 'ascend'],
        render: (_, item) => Formatter.formatCurrency(item.totalMarkup, 'USD'),
      },
      {
        title: 'Subtotal',
        dataIndex: 'subTotal',
        sortDirections: ['descend', 'ascend'],
        render: (_, item) => Formatter.formatCurrency(item.subTotal, 'USD'),
      },
    ];
  }, []);

  // const isDiscount = useMemo(() => {
  //   return item?.discount.isDiscount;
  // }, [item]);

  // const discount = useMemo(() => {
  //   if (!item?.discount.isDiscount) return 0;
  //   if (item.discount.type === ProposalDiscountType.fixed) {
  //     return item.discount.value;
  //   } else {
  //     return (item.discount.value * item.amount) / 100;
  //   }
  // }, [item]);

  // const salesTax = useMemo(() => {
  //   const tax = item?.salesTax ?? 0;
  //   if (tax > 0) {
  //     const filterTaxable = allItems.filter(l => !!l.taxable);
  //     const taxTotal = filterTaxable.reduce((total, line) => {
  //       return total + line.quantity * line.unitPrice;
  //     }, 0);
  //     const percentTx = R.toNumber(tax) ?? 0;
  //     return taxTotal * (percentTx / 100);
  //   }
  //   return 0;
  // }, [allItems, item]);

  const subAmt = useMemo(() => item?.amount ?? 0, [item]);

  const onSubmit = useCallback(
    data => {
      return of(null).pipe(
        switchMap(() => bloc.onSigned(coId, data.sign)),
        tap(() => subAmt > 0 && history.push(`/change-order-deposit?q=${param}`)),
      );
    },
    [bloc, coId, history, param, subAmt],
  );

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  if (loading.loading || !item) return <TopLoadingBar />;

  return (
    // <PDFPageLayout>
    <div className="page-content">
      <Container fluid>
        {loading.loading && <div className="mb-4">{/* <LoadingIndicator /> */}</div>}
        <Card>
          <div className="p-4" style={{ marginBottom: 0, flex: 1 }}>
            {/* {!R.isEmpty(timeMaterial.logo) && (
          <Row className="text-center align-items-center flex-column">
            <img src={timeMaterial.logo} className="logo" style={{ maxHeight: 100, height: 'auto' }} />
          </Row>
        )} */}
            <CardTitle className="mt-1 d-flex align-items-center flex-row flex-column font-size-20">Change Order</CardTitle>
            <div className="d-flex flex-column">
              <p className="mb-1">
                <strong>Organization: </strong> {item.organization?.name}
              </p>
              {!R.isEmpty(item.name) && (
                <p className="mb-1">
                  <strong>Work Order: </strong> {item.name}
                </p>
              )}
              <p className="mb-1">
                <strong>Order# </strong> {item?.orderNumber}
              </p>
              <p className="mb-1">
                <strong>Total: </strong> {Formatter.formatCurrency(COService.getChangeOrderTotalR(item), 'USD')}
              </p>
              {/* {!R.isEmpty(item?.billingAddress) && <p> {OrgService.getAddressString(item.billingAddress)}</p>} */}
            </div>
          </div>
          <Table columns={columns} dataSource={lItems} rowKey="id" />
          {/* <LaborLineItem coPreview={true} laborCost={laborCost as number} title="Labor Cost" /> */}
          <div className="d-flex justify-content-end mt-2 p-4">
            <div>
              {!!item.discount?.isDiscount && (
                <p className="mb-1">
                  <strong>Discount{item.discount.typeTitle ? ` (${item.discount.typeTitle})` : ''}: </strong> -
                  {Formatter.formatCurrency(item.discount.amount, 'USD')}
                  <span className="text-muted font-size-12 d-block">Taken off your next invoice.</span>
                </p>
              )}
              {/* {!!isDiscount && (
              <p className="mb-1">
                <strong>Discount: </strong> -{Formatter.formatCurrency(discount, 'USD')}
              </p>
            )}
            {salesTax != 0 && (
              <p className="mb-1">
                <strong>Sales Tax: </strong> {Formatter.formatCurrency(salesTax, 'USD')}
              </p>
            )} */}
              <p className="mb-1">
                <strong>Total: </strong> {Formatter.formatCurrency(COService.getChangeOrderTotalR(item), 'USD')}
              </p>
            </div>
          </div>
          <div className="d-flex justify-content-end mt-2 p-4">
            <div>
              {!!item.sign && (
                <>
                  <FormInput control={form.controls.sign} disabled />
                  {`Signed at ${Formatter.formatTime(item.signedAt)}`}
                </>
              )}
              {/* Only a change order sent for the client's signature can be signed */}
              {!item.sign && item.clientCanSign && (
                <>
                  <FormInput control={form.controls.sign} />
                  <Button type="submit" intent="primary" onClick={() => form.submit()} loading={submitting}>
                    Submit
                  </Button>
                </>
              )}
              {!item.sign && !item.clientCanSign && <span className="text-muted">No signature needed.</span>}
            </div>
          </div>
        </Card>
      </Container>
    </div>
    // </PDFPageLayout>
  );
});
